import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  type MarketingRouterSignificanceQuery,
  getMarketingRouterSignificanceTimeseries,
} from '~/services/PromoService';

export const marketingRouterSignificanceTimeseriesQueryKeys = {
  all: ['marketing-router-significance-timeseries'] as const,
  detail: (marketingRouterId: string, query: MarketingRouterSignificanceQuery) =>
    [...marketingRouterSignificanceTimeseriesQueryKeys.all, marketingRouterId, query] as const,
};

/**
 * The day-by-day series behind the results page graphs. Fetched separately
 * from the headline significance so the tables render as soon as the cheaper
 * aggregate query returns.
 */
export function useMarketingRouterSignificanceTimeseries({
  marketingRouterId,
  query,
  enabled = true,
}: {
  marketingRouterId: string | undefined;
  query: MarketingRouterSignificanceQuery;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: marketingRouterSignificanceTimeseriesQueryKeys.detail(marketingRouterId ?? '', query),
    queryFn: async ({ signal }) => {
      if (!marketingRouterId) {
        throw new Error('marketingRouterId is required');
      }
      const response = await getMarketingRouterSignificanceTimeseries(marketingRouterId, query, { signal });
      return response.result;
    },
    enabled: enabled && Boolean(marketingRouterId),
    // The results page re-queries under a different product scope; keep the
    // previous scope's numbers on screen until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}
